// Use SAM local for local development, custom domain for production
const API_BASE_URL = process.env.REACT_APP_ENV === 'development' 
  ? 'http://localhost:3000'  // SAM local API
  : '/api';  // API through CloudFront custom domain

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Create user in the database
  async createUser(userData) {
    return this.makeRequest('/create-user', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userData.user_id,
        email: userData.email,
        name: userData.name,
        picture: userData.picture
      }),
    });
  }

  // Get all available bees
  async getAllBees() {
    return this.makeRequest('/get-all-bees', {
      method: 'GET',
    });
  }

  // Get user's bees
  async getUserBees(user_id) {
    return this.makeRequest('/get-user-bees', {
      method: 'POST',
      body: JSON.stringify({ user_id }),
    });
  }

  // Add user to a bee
  async addUserToBee(user_id, bee_id) {
    return this.makeRequest('/add-user-to-bee', {
      method: 'POST',
      body: JSON.stringify({
        user_id,
        bee_id,
      }),
    });
  }

  // Get next word for practice (orchestrates between official and off-list words)
  async getNextWord(user_id, bee_id) {
    return this.makeRequest('/get-word', {
      method: 'POST',
      body: JSON.stringify({
        user_id,
        bee_id,
      }),
    });
  }

  // Submit attempt result
  async writeAttempt(user_id, bee_id, word, result) {
    return this.makeRequest('/write-attempt', {
      method: 'POST',
      body: JSON.stringify({
        user_id,
        bee_id,
        word,
        result,
      }),
    });
  }

  // Reset user's attempts for a bee
  async resetUserBeeAttempts(user_id, bee_id) {
    return this.makeRequest('/reset-user-bee-attempts', {
      method: 'POST',
      body: JSON.stringify({
        user_id,
        bee_id,
      }),
    });
  }

  // Admin: Create new bee with words
  async adminCreateBee(beeData) {
    return this.makeRequest('/admin-create-bee', {
      method: 'POST',
      body: JSON.stringify(beeData),
    });
  }

  // Competitor Management
  async createCompetitor(user_id, name, email) {
    const body = {
      user_id,
      name,
    };
    
    // Only include email if it's provided
    if (email && email.trim()) {
      body.email = email.trim();
    }
    
    return this.makeRequest('/create-competitor', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async deleteCompetitor(user_id, competitor_id) {
    return this.makeRequest('/delete-competitor', {
      method: 'POST',
      body: JSON.stringify({
        user_id,
        competitor_id,
      }),
    });
  }

  async getUserCompetitors(user_id) {
    return this.makeRequest('/get-user-competitors', {
      method: 'POST',
      body: JSON.stringify({ user_id }),
    });
  }

  async addCompetitorToBee(competitor_id, bee_id) {
    return this.makeRequest('/add-competitor-to-bee', {
      method: 'POST',
      body: JSON.stringify({
        competitor_id,
        bee_id,
      }),
    });
  }

  // Competitor-based practice methods
  async getNextWordForCompetitor(competitor_id, bee_id) {
    return this.makeRequest('/get-word', {
      method: 'POST',
      body: JSON.stringify({
        competitor_id,
        bee_id,
      }),
    });
  }

  async writeAttemptForCompetitor(competitor_id, bee_id, word, result, requesting_user_id) {
    return this.makeRequest('/write-attempt', {
      method: 'POST',
      body: JSON.stringify({
        competitor_id,
        bee_id,
        word,
        result,
        requesting_user_id,
      }),
    });
  }

  // Competitor Sharing methods
  async shareCompetitor(shareData) {
    return this.makeRequest('/share-competitor', {
      method: 'POST',
      body: JSON.stringify(shareData),
    });
  }

  async revokeCompetitorAccess(revokeData) {
    return this.makeRequest('/revoke-competitor-access', {
      method: 'POST',
      body: JSON.stringify(revokeData),
    });
  }

  async getSharedCompetitors(user_id) {
    return this.makeRequest(`/get-shared-competitors?user_id=${encodeURIComponent(user_id)}`, {
      method: 'GET',
    });
  }

  async getCompetitorAccessList(competitor_id, requesting_user_id) {
    return this.makeRequest(`/get-competitor-access-list?competitor_id=${encodeURIComponent(competitor_id)}&requesting_user_id=${encodeURIComponent(requesting_user_id)}`, {
      method: 'GET',
    });
  }

  async updateCompetitorCurrentBee(competitor_id, bee_id, requesting_user_id) {
    return this.makeRequest('/update-competitor-current-bee', {
      method: 'POST',
      body: JSON.stringify({
        competitor_id,
        bee_id,
        requesting_user_id,
      }),
    });
  }

  async getCompetitorAnalysis(competitor_id, requesting_user_id) {
    return this.makeRequest('/get-competitor-analysis', {
      method: 'POST',
      body: JSON.stringify({
        competitor_id,
        requesting_user_id,
      }),
    });
  }

  async getCompetitorMisspelledWords(competitor_id, bee_id, requesting_user_id) {
    return this.makeRequest('/get-competitor-analysis', {
      method: 'POST',
      body: JSON.stringify({
        competitor_id,
        bee_id,
        requesting_user_id,
        mode: 'misspelled_words'
      }),
    });
  }

  async getCompetitorWordHistory(competitor_id, bee_id, requesting_user_id) {
    return this.makeRequest('/get-competitor-analysis', {
      method: 'POST',
      body: JSON.stringify({
        competitor_id,
        bee_id,
        requesting_user_id,
        mode: 'all_words'
      }),
    });
  }
}

export default new ApiService();